import { AGENT_TOOL_TRANSITION_TYPE, TransitionModel } from '../Models/FlowModel';
import { FlowEdge, FlowNode } from '../Models/GraphModel';

const agentToolTransitionType = AGENT_TOOL_TRANSITION_TYPE.toLowerCase();
const noNodes: FlowNode[] = [];

function getAgentBoundaries(nodes: FlowNode[]): Set<string> {
	return new Set(nodes.filter((node) => node.data.Type === 'AiAgent.Prompt').map((node) => node.id));
}

export function isAgentToolTransition(edge: Pick<FlowEdge, 'data'>): boolean {
	return String((edge.data as TransitionModel | undefined)?.Type ?? '').toLowerCase() === agentToolTransitionType;
}

/** Returns the tool roots and activities reachable through their ordinary transitions. */
export function getAgentToolActivityIds(edges: FlowEdge[], nodes: FlowNode[] = noNodes): Set<string> {
	const boundaries = getAgentBoundaries(nodes);
	const outgoingEdges = new Map<string, FlowEdge[]>();
	for (const edge of edges) {
		const outgoing = outgoingEdges.get(edge.source) ?? [];
		outgoing.push(edge);
		outgoingEdges.set(edge.source, outgoing);
	}

	const activityIds = new Set(edges.filter(isAgentToolTransition).map((edge) => edge.target));
	const pending = Array.from(activityIds);

	while (pending.length > 0) {
		const currentId = pending.pop()!;
		if (boundaries.has(currentId)) continue;
		for (const edge of outgoingEdges.get(currentId) ?? []) {
			if (isAgentToolTransition(edge) || activityIds.has(edge.target)) {
				continue;
			}

			activityIds.add(edge.target);
			pending.push(edge.target);
		}
	}

	return activityIds;
}

/** Returns the agent-tool transition that owns an activity in the reachable tool graph. */
export function getAgentToolTransitionForActivity(edges: FlowEdge[], activityId: string | undefined, nodes: FlowNode[] = noNodes): FlowEdge | undefined {
	return getAgentToolTransitionsForActivity(edges, activityId, nodes)[0];
}

/** Returns every agent-tool transition that can reach an activity. */
export function getAgentToolTransitionsForActivity(edges: FlowEdge[], activityId: string | undefined, nodes: FlowNode[] = noNodes): FlowEdge[] {
	if (!activityId) return [];
	const boundaries = getAgentBoundaries(nodes);

	const outgoingEdges = new Map<string, FlowEdge[]>();
	for (const edge of edges) {
		const outgoing = outgoingEdges.get(edge.source) ?? [];
		outgoing.push(edge);
		outgoingEdges.set(edge.source, outgoing);
	}

	const pending = edges.filter(isAgentToolTransition).map((edge) => ({ activityId: edge.target, toolEdge: edge }));
	const visited = new Set<string>();
	const owners = new Map<string, FlowEdge>();
	while (pending.length > 0) {
		const current = pending.shift()!;
		if (current.activityId === activityId) {
			const ownerKey = current.toolEdge.id || `${current.toolEdge.source}:${current.toolEdge.target}`;
			owners.set(ownerKey, current.toolEdge);
			continue;
		}
		const ownerKey = current.toolEdge.id || `${current.toolEdge.source}:${current.toolEdge.target}`;
		const visitKey = `${ownerKey}:${current.activityId}`;
		if (visited.has(visitKey)) continue;
		visited.add(visitKey);
		if (boundaries.has(current.activityId)) continue;

		for (const edge of outgoingEdges.get(current.activityId) ?? []) {
			if (!isAgentToolTransition(edge)) {
				pending.push({ activityId: edge.target, toolEdge: current.toolEdge });
			}
		}
	}

	return Array.from(owners.values());
}

// Array identities change on drag/selection. Cache structural signatures per snapshot,
// then retain a bounded set of results shared by equivalent graph snapshots.
const edgeStructures = new WeakMap<FlowEdge[], string>();
const boundaryMemberships = new WeakMap<FlowNode[], string>();
const toolIdsByStructure = new Map<string, Set<string>>();
const snapshotResults = new WeakMap<FlowEdge[], WeakMap<FlowNode[], Set<string>>>();
const maximumCachedStructures = 32;

export function getCachedAgentToolActivityIds(edges: FlowEdge[], nodes: FlowNode[] = noNodes): Set<string> {
	let byNodes = snapshotResults.get(edges);
	const snapshotResult = byNodes?.get(nodes);
	if (snapshotResult) {
		return snapshotResult;
	}
	let edgeStructure = edgeStructures.get(edges);
	if (edgeStructure === undefined) {
		edgeStructure = JSON.stringify(edges.map((edge) => JSON.stringify([edge.source, edge.target, isAgentToolTransition(edge)])).sort());
		edgeStructures.set(edges, edgeStructure);
	}
	let boundaries = boundaryMemberships.get(nodes);
	if (boundaries === undefined) {
		boundaries = JSON.stringify(Array.from(getAgentBoundaries(nodes)).sort());
		boundaryMemberships.set(nodes, boundaries);
	}
	const key = JSON.stringify([edgeStructure, boundaries]);
	let ids = toolIdsByStructure.get(key);
	if (!ids) {
		ids = getAgentToolActivityIds(edges, nodes);
		if (toolIdsByStructure.size >= maximumCachedStructures) {
			toolIdsByStructure.delete(toolIdsByStructure.keys().next().value!);
		}
		toolIdsByStructure.set(key, ids);
	}
	if (!byNodes) {
		byNodes = new WeakMap();
		snapshotResults.set(edges, byNodes);
	}
	byNodes.set(nodes, ids);
	return ids;
}
